import { useState } from 'react';
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle, MessageCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    wants_to_volunteer: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-[#FDFBF7] py-24" data-testid="contact-success">
        <div className="max-w-md mx-auto text-center px-6">
          <div className="w-20 h-20 bg-[#22C55E] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-[#1E293B] font-['Fredoka'] mb-4">
            Message Sent!
          </h2>
          <p className="text-lg text-[#64748B] mb-8">
            Thank you for reaching out. We'll get back to you as soon as possible.
          </p>
          <button
            onClick={() => {
              setIsSubmitted(false);
              setFormData({ name: '', email: '', message: '', wants_to_volunteer: false });
            }}
            data-testid="send-another-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FACC15] text-[#422006] font-bold px-8 py-4 hover:-translate-y-1 transition-all duration-200"
          >
            Send Another Message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#FDFBF7]" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="font-accent text-2xl text-[#FF7A59]">Get in Touch</span>
            <h1 className="text-5xl md:text-6xl font-bold text-[#1E293B] font-['Fredoka'] mt-2 mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-[#64748B] leading-relaxed">
              Have questions about Protopia or want to get involved? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-[#1E293B] font-['Fredoka'] mb-6">
                Send Us a Message
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-[#1E293B] font-semibold">
                    Name <span className="text-[#FF7A59]">*</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="contact-name-input"
                    className="mt-2 rounded-xl border-[#E2E8F0] focus:ring-2 focus:ring-[#FACC15]/50 focus:border-[#FACC15]"
                    placeholder="Your name"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-[#1E293B] font-semibold">
                    Email <span className="text-[#FF7A59]">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="contact-email-input"
                    className="mt-2 rounded-xl border-[#E2E8F0] focus:ring-2 focus:ring-[#FACC15]/50 focus:border-[#FACC15]"
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#1E293B] font-semibold">
                    Message <span className="text-[#FF7A59]">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    data-testid="contact-message-input"
                    className="mt-2 rounded-xl border-[#E2E8F0] focus:ring-2 focus:ring-[#FACC15]/50 focus:border-[#FACC15] min-h-[150px]"
                    placeholder="Tell us how we can help..."
                    required
                  />
                </div>

                <div className="flex items-center gap-3">
                  <Checkbox
                    id="volunteer"
                    checked={formData.wants_to_volunteer}
                    onCheckedChange={(checked) => setFormData({ ...formData, wants_to_volunteer: checked })}
                    data-testid="contact-volunteer-checkbox"
                  />
                  <Label htmlFor="volunteer" className="text-[#64748B] cursor-pointer">
                    I'm interested in volunteering with Protopia
                  </Label>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  data-testid="contact-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#FACC15] text-[#422006] font-bold px-8 py-4 shadow-[0_4px_14px_0_rgba(250,204,21,0.39)] hover:shadow-[0_6px_20px_rgba(250,204,21,0.23)] hover:-translate-y-1 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold text-[#1E293B] font-['Fredoka'] mb-6">
                Contact Information
              </h2>
              
              <div className="space-y-6 mb-10">
                <a 
                  href="mailto:protopiatukunn@gmail.com"
                  className="flex items-start gap-4 p-4 rounded-2xl bg-[#FDFBF7] hover:bg-[#F3F4F6] transition-colors"
                  data-testid="contact-email-link"
                >
                  <div className="w-12 h-12 bg-[#38BDF8] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1E293B]">Email</h3>
                    <p className="text-[#64748B]">protopiatukunn@gmail.com</p>
                  </div>
                </a>

                <a 
                  href="tel:+15126997392"
                  className="flex items-start gap-4 p-4 rounded-2xl bg-[#FDFBF7] hover:bg-[#F3F4F6] transition-colors"
                  data-testid="contact-phone-link"
                >
                  <div className="w-12 h-12 bg-[#22C55E] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1E293B]">Phone</h3>
                    <p className="text-[#64748B]">512-699-7392</p>
                  </div>
                </a>

                <a 
                  href="https://wa.me/17373860709"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 p-4 rounded-2xl bg-[#FDFBF7] hover:bg-[#F3F4F6] transition-colors"
                  data-testid="contact-whatsapp-link"
                >
                  <div className="w-12 h-12 bg-[#25D366] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1E293B]">WhatsApp</h3>
                    <p className="text-[#64748B]">+1 737-386-0709</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-4 rounded-2xl bg-[#FDFBF7]">
                  <div className="w-12 h-12 bg-[#FF7A59] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1E293B]">Location</h3>
                    <p className="text-[#64748B]">Nairobi, Kenya</p>
                  </div>
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-[#FACC15]/10 rounded-2xl p-6">
                <h3 className="font-bold text-[#1E293B] mb-3">Want to Volunteer?</h3>
                <p className="text-[#64748B] mb-4">
                  Check the volunteer box above, or visit our dedicated volunteer page to learn more about opportunities.
                </p>
                <a 
                  href="/volunteer"
                  data-testid="contact-volunteer-link"
                  className="inline-flex items-center gap-2 text-[#38BDF8] font-bold hover:underline"
                >
                  Visit Volunteer Page →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
